import { Menu } from "@dhx/ts-menu";
import { IToolbar } from "@dhx/ts-toolbar";
import { DataPage } from "@xbs/muon";
import { ICellMeta, IFormats, IPageMeta, ISpreadsheetConfig } from "../types";
import locale from "./../locales/en";
import { getDefaultFormatsMap, getFormat, getFormattedValue } from "./format";
import { IGrid } from "@dhx/ts-grid";
import { getCellInfo, getCellsArray, isCellSpaned } from "./main";

declare const dhx: any;

export function getColorpickerTemplate(color: string, icon: string): string {
	return `<div class="dxi dxi-${icon} dhx_toolbar-button__icon dhx_toolbar-button__colorpicker" style="border-bottom: solid 3px ${color}"></div>`;
}

export function updateToolbar(toolbar: IToolbar, menu: Menu, page: DataPage, grid) {
	const { merged, focusedCell, selectedCells } = page.getMeta();
	if (!selectedCells || !focusedCell) return;
	const selected = selectedCells.join(",");
	const cellInfo: ICellMeta = {
		locked: false,
		format: getFormat("common"),
		...getCellInfo(focusedCell.cell, page, grid),
	};
	let css: string[] = [];


	const cells = getCellsArray(selected, grid);
	let isNotSpaned = !!cells.find(c => !isCellSpaned(c, merged, grid));

	if (cellInfo.css) {
		css = typeof cellInfo.css === "string" ? cellInfo.css.split(" ") : cellInfo.css;
	}

	let customCssStyles = {};
	for (const styles of css) {
		customCssStyles = { ...customCssStyles, ...dhx.css.get(styles) };
	}
	const styles = {
		color: "#4C4C4C",
		background: "#FFF",
		"text-align": "left",
		"vertical-align": "center",
		"font-style": "",
		"font-weight": "",
		"text-decoration": "",
		multiline: "clip",
		...customCssStyles,
	};
	const updateStyles = css => {
		for (const key in css) {
			switch (key) {
				case "color":
					toolbar.data.update(key, {
						html: getColorpickerTemplate(css[key] || "#4C4C4C", "format-color-text"),
					});
					break;
				case "background":
				case "background-color":
					toolbar.data.update("background", {
						html: getColorpickerTemplate(css[key] || "#FFF", "format-color-fill"),
					});
					break;
				case "text-align":
					if (!toolbar.data.exists("halign")) return;
					const halign = `halign-${css[key]}`;
					toolbar.data.update("halign", { icon: toolbar.data.getItem(halign).icon });
					const conf_halign = {};
					const parent_halign = toolbar.data.getItem("halign");
					parent_halign.items.forEach(i => {
						conf_halign[i.id] = false;
						menu.data.update(i.id, { active: i.id === halign });
					});
					conf_halign[halign] = true;
					toolbar.setState(conf_halign);
					break;
				case "vertical-align":
					if (!toolbar.data.exists("valign")) return;
					const valign = `valign-${css[key]}`;
					const conf_valign = {};
					const parent_valign = toolbar.data.getItem("valign");
					parent_valign.items.forEach(i => {
						conf_valign[i.id] = false;
						menu.data.update(i.id, { active: i.id === valign });
					});
					conf_valign[valign] = true;
					toolbar.setState(conf_valign);
					toolbar.data.update("valign", { icon: toolbar.data.getItem(valign).icon });
					break;
				case "multiline":
					if (!toolbar.data.exists("multiline")) return;
					const multiline = `multiline-${css[key]}`;
					const conf_multiline = {};
					const parent_multiline = toolbar.data.getItem("multiline");
					parent_multiline.items.forEach(i => {
						conf_multiline[i.id] = false;
						menu.data.update(i.id, { active: i.id === multiline });
					});
					conf_multiline[multiline] = true;
					toolbar.setState(conf_multiline);
					toolbar.data.update("multiline", { icon: toolbar.data.getItem(multiline).icon });
					break;
				case "font-weight":
					toolbar.data.update("font-weight-bold", {
						active: css[key] == "bold" || css[key] == "700",
					});
					menu.data.update("font-weight-bold", { active: css[key] == "bold" || css[key] == "700" });
					break;
				case "font-style":
					toolbar.data.update("font-style-italic", {
						active: css[key] === "italic",
					});
					menu.data.update("font-style-italic", { active: css[key] === "italic" });
					break;
				case "text-decoration":
					const uderlineConfig = { active: css[key].includes("underline") };
					toolbar.data.update("text-decoration-underline", uderlineConfig);
					menu.data.update("text-decoration-underline", uderlineConfig);

					const lineThroughConfig = { active: css[key].includes("line-through") };
					toolbar.data.update("text-decoration-line-through", lineThroughConfig);
					menu.data.update("text-decoration-line-through", lineThroughConfig);
					break;
				default:
					break;
			}
		}
	};

	const text = locale[isNotSpaned ? "merge" : "unmerge"];
	const mergeIcon = `dxi dxi-table-${isNotSpaned ? "merge" : "split"}-cell${isNotSpaned ? "s" : ""}`
	toolbar.data.update("merge", {
		active: !isNotSpaned,
		icon: mergeIcon,
		tooltip: text,
	});
	menu.data.update("merge", {
		value: text,
		tooltip: text,
	});
	updateStyles(styles);
	// reset format menu state
	const formatItem = toolbar.data.getItem("format");
	if (formatItem) {
		formatItem.items.map(item => (item.active = false));
	}

	for (const key in cellInfo) {
		switch (key) {
			case "locked":
				if (toolbar.data.getItem("lock")) {
					toolbar.data.update("lock", {
						active: cellInfo[key],
					});
				}
				break;
			case "format":
				// eslint-disable-next-line no-case-declarations
				const id = getDefaultFormatsMap()[(cellInfo[key] as IFormats).mask] || "common";
				toolbar.data.getItem("format") &&
					toolbar.data.update("format", {
						value: getFormat(id).name || id,
					});
				if (toolbar.data.getItem(id)) {
					toolbar.data.update(id, {
						active: true,
					});
				}
				const formats = menu.data.getItems("format");
				formats.forEach(f => {
					menu.data.update(f.id as string, { active: id === f.id });
				});
				break;
			default:
				break;
		}
	}

	for (const key in page) {
		switch (key) {
			case "filter":
				if (toolbar.data.getItem("filter")) {
					toolbar.data.update("filter", {
						active: page[key],
					});
				}
				break;
		}
	} 
}

export function getToggledValue(page: DataPage, cell: string, name: string, value: any, grid: IGrid): string {
	const info = getCellInfo(cell, page, grid);
	let css = [];

	if (info.css) {
		css = typeof info.css === "string" ? info.css.split(" ") : info.css;
	}

	let customCssStyles = {};
	for (const styles of css) {
		customCssStyles = { ...customCssStyles, ...dhx.css.get(styles) };
	}
	const v = customCssStyles[name];
	let prevValue = !v || v  === "none" ? "" : v;
	let newVal = prevValue.includes(value) ? prevValue.split(value).join("") : `${prevValue} ${value}`;
	if (name === "text-decoration" && !newVal) {
		newVal = "none";
	} 
	return newVal.trim();
}

export function getFormatItem(format: IFormats, example = "1500.31"): string {
	const { name } = format;
	const val = getFormattedValue(example, format);
	return `<div class="dhx_format-item"><span class="dhx_format-name">${locale[name as string] ||
		name} </span><span class="dhx_format-helper">${val}</span></div>`;
}

export function getFormatsDropdown(config: ISpreadsheetConfig) {
	const formats = config.formats as IFormats [];
	return formats.map(format => ({
		id: format.id,
		css: "dhx_format-name-wrap",
		twoState: true,
		group: "format",
		html: getFormatItem(format, format.example),
	}));
}
